import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  fetchCheckInParticipants,
  manualCheckIn,
  parishFetchCheckInParticipants,
  parishManualCheckIn,
  parishPostCheckInClientLogs,
  parishScanCheckInCode,
  parishUndoCheckIn,
  postCheckInClientLogs,
  scanCheckInCode,
  undoCheckIn,
  type CheckInParticipant,
  type CheckInScanResult,
  type CheckInStats,
} from '../../services/checkInService';
import {
  checkInLog,
  clearCheckInLogsLocal,
  configureCheckInLogger,
  exportCheckInLogsText,
  flushCheckInLogs,
  getCheckInLogSessionId,
  getCheckInLogs,
  redactCode,
  startTimedCheckInLog,
  subscribeCheckInLogs,
  type CheckInLogEntry,
} from '../../utils/checkInClientLogger';
import {
  clearReaderElement,
  ensureCameraPermission,
  startCheckInCameraEngine,
  waitForVisibleReader,
  type CheckInCameraEngine,
} from '../../utils/checkInCameraEngine';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BugReportIcon from '@mui/icons-material/BugReport';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

function playScanFeedback(kind: 'detected' | 'success' | 'warn' | 'error') {
  try {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      if (kind === 'detected') navigator.vibrate(40);
      else if (kind === 'success') navigator.vibrate([40, 40, 80]);
      else if (kind === 'warn') navigator.vibrate([60, 40, 60]);
      else navigator.vibrate([120, 60, 120]);
    }
  } catch {
    /* ignore */
  }

  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    if (kind === 'detected') {
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.04, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
      osc.start(now);
      osc.stop(now + 0.08);
    } else if (kind === 'success') {
      osc.frequency.value = 1200;
      gain.gain.setValueAtTime(0.06, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
      osc.start(now);
      osc.stop(now + 0.18);
    } else if (kind === 'warn') {
      osc.frequency.value = 520;
      gain.gain.setValueAtTime(0.05, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
      osc.start(now);
      osc.stop(now + 0.22);
    } else {
      osc.frequency.value = 280;
      gain.gain.setValueAtTime(0.06, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
      osc.start(now);
      osc.stop(now + 0.28);
    }
    void ctx.resume();
    setTimeout(() => void ctx.close(), 400);
  } catch {
    /* ignore audio failures */
  }
}

function pauseScanner(engine: CheckInCameraEngine | null) {
  try {
    engine?.pause();
  } catch {
    /* ignore */
  }
}

function resumeScanner(engine: CheckInCameraEngine | null) {
  try {
    engine?.resume();
  } catch {
    /* ignore */
  }
}

const NO_DECODE_HINT_MS = 4000;

function isCheckInDebugUiEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).get('checkinDebug') === '1') return true;
    if (window.localStorage.getItem('checkin_debug_logs') === '1') return true;
  } catch {
    /* ignore */
  }
  return false;
}

interface EventCheckInScannerProps {
  eventId?: string;
  parishMode?: boolean;
  primaryColor?: string;
  onStatsRefresh?: () => void;
}

function normalizeCode(raw: string): string {
  const uuid =
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.exec(raw)?.[0];
  if (uuid) return uuid.toLowerCase();
  return raw.trim().replace(/\s+/g, '');
}

function errorToText(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`.trim();
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const record = err as Record<string, unknown>;
    const name = typeof record.name === 'string' ? record.name : '';
    const message = typeof record.message === 'string' ? record.message : '';
    const joined = `${name} ${message}`.trim();
    if (joined) return joined;
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }
  return String(err || '');
}

function getCameraEnvironmentHint(): string | null {
  if (typeof window === 'undefined') return null;
  if (window.isSecureContext) return null;
  const { protocol, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return null;
  if (protocol === 'http:') {
    return 'Conexão insegura (HTTP). No celular, a câmera só funciona com HTTPS — ou teste no PC em http://localhost:3001';
  }
  return 'A câmera exige HTTPS. Abra o link com https://';
}

function getCameraErrorMessage(err: unknown): string {
  const envHint = getCameraEnvironmentHint();
  if (envHint) return envHint;

  const text = errorToText(err);
  const combined = text.toLowerCase();
  if (combined.includes('notallowed') || combined.includes('permission') || combined.includes('denied')) {
    return 'Permissão da câmera negada. Toque em “Permitir” ou libere em Ajustes do navegador → Câmera.';
  }
  if (combined.includes('notfound') || combined.includes('no camera') || combined.includes('devicesnotfound')) {
    return 'Nenhuma câmera encontrada neste dispositivo.';
  }
  if (combined.includes('notreadable') || combined.includes('in use') || combined.includes('could not start')) {
    return 'A câmera está em uso por outro app. Feche outros apps e tente de novo.';
  }
  if (combined.includes('cannot find module') || combined.includes('html5-qrcode')) {
    return 'Biblioteca de QR não instalada. Rode: docker compose --profile dev exec frontend-dev npm install html5-qrcode';
  }
  if (combined.includes('qr box') || combined.includes('qrbox') || combined.includes('viewfinder')) {
    return 'Não foi possível ajustar a área de leitura. Tente novamente ou use o código manual.';
  }
  if (
    combined.includes('not supported') ||
    combined.includes('getusermedia') ||
    combined.includes('streaming not supported') ||
    combined.includes('barcodedetector')
  ) {
    return 'Este navegador não suporta câmera para QR. Use Chrome/Safari atualizado ou digite o código.';
  }
  if (combined.includes('not be found') || combined.includes('element') || combined.includes('visível')) {
    return 'Não foi possível preparar a área da câmera. Toque em “Usar câmera” novamente.';
  }
  const detail = text && text.length < 160 ? ` Detalhe: ${text}` : '';
  return `Não foi possível acessar a câmera. Verifique permissão do navegador e use HTTPS.${detail}`;
}

const EventCheckInScanner: React.FC<EventCheckInScannerProps> = ({
  eventId,
  parishMode = false,
  primaryColor = '#4b1b7a',
  onStatsRefresh,
}) => {
  const readerId = useId().replace(/:/g, '');
  const readerElementId = `checkin-qr-reader-${readerId}`;
  const [codeInput, setCodeInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraMounting, setCameraMounting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState<CheckInScanResult | null>(null);
  const [scanStatus, setScanStatus] = useState<'idle' | 'detected' | 'processing'>('idle');
  const [statusFlash, setStatusFlash] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'checked' | 'pending'>('pending');
  const [participants, setParticipants] = useState<CheckInParticipant[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logEntries, setLogEntries] = useState<CheckInLogEntry[]>([]);
  const [flushStatus, setFlushStatus] = useState<string | null>(null);
  const [noDecodeHint, setNoDecodeHint] = useState(false);
  const [cameraEngineKind, setCameraEngineKind] = useState<string | null>(null);
  const [liveDecodeCount, setLiveDecodeCount] = useState(0);
  const [debugUi] = useState(() => isCheckInDebugUiEnabled());
  const cameraEnvHint = getCameraEnvironmentHint();

  const qrRef = useRef<HTMLDivElement | null>(null);
  const cameraEngineRef = useRef<CheckInCameraEngine | null>(null);
  const scanInputTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScannedRef = useRef<{ code: string; at: number }>({ code: '', at: 0 });
  const checkInQueueRef = useRef<Promise<void>>(Promise.resolve());
  const searchAbortRef = useRef<AbortController | null>(null);
  const decodeCountRef = useRef(0);
  const busyRef = useRef(false);
  const lastDedupLogAtRef = useRef(0);
  const noDecodeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    configureCheckInLogger({
      eventId,
      flush: async (payload) => {
        if (parishMode && eventId) {
          await parishPostCheckInClientLogs(eventId, payload);
        } else {
          await postCheckInClientLogs(payload);
        }
      },
    });
    checkInLog('info', 'scanner_mount', 'Scanner montado', {
      eventId,
      parishMode,
      sessionId: getCheckInLogSessionId(),
      envHint: getCameraEnvironmentHint(),
    });
    return () => {
      checkInLog('info', 'scanner_unmount', 'Scanner desmontado');
      void flushCheckInLogs();
    };
  }, [eventId, parishMode]);

  useEffect(() => {
    if (!debugUi || !logsOpen) return;
    setLogEntries(getCheckInLogs());
    return subscribeCheckInLogs(() => setLogEntries(getCheckInLogs()));
  }, [debugUi, logsOpen]);

  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  const resultClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultBannerRef = useRef<HTMLDivElement | null>(null);

  const showResult = useCallback((result: CheckInScanResult) => {
    if (resultClearTimer.current) clearTimeout(resultClearTimer.current);
    setLastResult(result);
    const kind =
      result.success ? 'success' : result.error === 'ALREADY_CHECKED_IN' ? 'warn' : 'error';
    playScanFeedback(kind);
    requestAnimationFrame(() => {
      resultBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    if (result.success) {
      resultClearTimer.current = setTimeout(() => {
        setLastResult(null);
        setScanStatus('idle');
        setStatusFlash(null);
        resumeScanner(cameraEngineRef.current);
      }, 3500);
    }
  }, []);

  const doScan = useCallback(
    async (rawCode: string) => {
      const code = normalizeCode(rawCode);
      if (!code) {
        checkInLog('warn', 'scan_empty', 'Código vazio após normalize', {
          rawLen: String(rawCode || '').length,
        });
        return;
      }

      const now = Date.now();
      if (lastScannedRef.current.code === code && now - lastScannedRef.current.at < 2500) {
        if (now - lastDedupLogAtRef.current > 1500) {
          lastDedupLogAtRef.current = now;
          checkInLog('debug', 'scan_deduped', 'Leitura ignorada (debounce 2.5s)', {
            code: redactCode(code),
            sinceMs: now - lastScannedRef.current.at,
          });
        }
        return;
      }
      lastScannedRef.current = { code, at: now };

      // Feedback imediato: QR foi lido (antes da API)
      setScanStatus('detected');
      setStatusFlash('QR lido — validando…');
      setLastResult(null);
      playScanFeedback('detected');
      pauseScanner(cameraEngineRef.current);

      const timed = startTimedCheckInLog('scan_api', 'Enviando check-in à API', {
        code: redactCode(code),
        parishMode,
        eventId,
        online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      });
      setBusy(true);
      busyRef.current = true;
      setScanStatus('processing');
      try {
        const result =
          parishMode && eventId
            ? await parishScanCheckInCode(eventId, code)
            : await scanCheckInCode(code);
        setStatusFlash(null);
        setScanStatus('idle');
        showResult(result);
        timed.end(result.success ? 'info' : 'warn', result.message || 'Resposta API', {
          success: result.success,
          error: result.error,
          participant: result.data?.participantName,
        });
        if (result.success) {
          setCodeInput('');
          onStatsRefresh?.();
        } else {
          setTimeout(() => resumeScanner(cameraEngineRef.current), 1200);
        }
      } catch (err: any) {
        const data = err?.response?.data;
        const fail = data || { success: false, message: 'Erro ao processar check-in.' };
        setStatusFlash(null);
        setScanStatus('idle');
        showResult(fail);
        timed.end('error', 'Falha HTTP no scan', {
          status: err?.response?.status,
          message: data?.message || errorToText(err),
          error: data?.error,
        });
        setTimeout(() => resumeScanner(cameraEngineRef.current), 1200);
      } finally {
        setBusy(false);
        busyRef.current = false;
      }
    },
    [eventId, onStatsRefresh, parishMode, showResult]
  );

  const enqueueScan = useCallback(
    (code: string) => {
      if (busyRef.current) {
        checkInLog('debug', 'scan_queued_while_busy', 'Leitura enfileirada enquanto API em andamento', {
          code: redactCode(code),
        });
      }
      checkInQueueRef.current = checkInQueueRef.current.then(() => doScan(code));
    },
    [doScan]
  );

  const clearNoDecodeWatchdog = useCallback(() => {
    if (noDecodeTimerRef.current) {
      clearTimeout(noDecodeTimerRef.current);
      noDecodeTimerRef.current = null;
    }
  }, []);

  const armNoDecodeWatchdog = useCallback(() => {
    clearNoDecodeWatchdog();
    setNoDecodeHint(false);
    noDecodeTimerRef.current = setTimeout(() => {
      if (decodeCountRef.current > 0) return;
      setNoDecodeHint(true);
      setSearchOpen(true);
      checkInLog('warn', 'no_decode_hint', 'Câmera ativa sem leitura — sugerindo busca por nome', {
        afterMs: NO_DECODE_HINT_MS,
        engine: cameraEngineRef.current?.kind,
      });
    }, NO_DECODE_HINT_MS);
  }, [clearNoDecodeWatchdog]);

  const stopCamera = useCallback(async () => {
    clearNoDecodeWatchdog();
    checkInLog('info', 'camera_stop', 'Parando câmera', {
      hadScanner: !!cameraEngineRef.current,
      decodeCount: decodeCountRef.current,
      engine: cameraEngineRef.current?.kind,
    });
    if (cameraEngineRef.current) {
      try {
        await cameraEngineRef.current.stop();
      } catch (err) {
        checkInLog('warn', 'camera_stop_error', 'Erro ao parar câmera', { error: errorToText(err) });
      }
      cameraEngineRef.current = null;
    }
    setCameraOn(false);
    setCameraMounting(false);
    setCameraEngineKind(null);
    setNoDecodeHint(false);
    setLiveDecodeCount(0);
    if (qrRef.current) clearReaderElement(qrRef.current);
  }, [clearNoDecodeWatchdog]);

  const startCamera = useCallback(async () => {
    if (cameraOn || cameraMounting) {
      await stopCamera();
      return;
    }

    const envHint = getCameraEnvironmentHint();
    if (envHint) {
      checkInLog('error', 'camera_insecure', envHint);
      setLastResult({ success: false, message: envHint });
      return;
    }

    const timed = startTimedCheckInLog('camera_pipeline', 'Abrindo câmera (permissão + start)');
    setScanning(true);
    setLastResult(null);
    setNoDecodeHint(false);
    flushSync(() => {
      setCameraMounting(true);
    });

    try {
      await waitForVisibleReader(readerElementId);
      checkInLog('debug', 'camera_dom_ready', 'Elemento da câmera visível');

      if (qrRef.current) clearReaderElement(qrRef.current);

      const permTimed = startTimedCheckInLog('camera_permission', 'Solicitando permissão getUserMedia');
      await ensureCameraPermission();
      permTimed.end('info', 'Permissão OK');

      decodeCountRef.current = 0;
      setLiveDecodeCount(0);
      const engine = await startCheckInCameraEngine(
        readerElementId,
        (decoded: string) => {
          enqueueScan(decoded);
        },
        ({ count, engine: kind, codeLen }) => {
          decodeCountRef.current = count;
          setLiveDecodeCount(count);
          if (count === 1) {
            setNoDecodeHint(false);
            clearNoDecodeWatchdog();
          }
          if (count === 1 || count % 10 === 0) {
            checkInLog('debug', 'qr_decoded', `Frame decodificado #${count}`, {
              codeLen,
              engine: kind,
              busy: busyRef.current,
            });
          }
        }
      );
      cameraEngineRef.current = engine;
      setCameraEngineKind(engine.kind);
      setCameraOn(true);
      armNoDecodeWatchdog();
      timed.end('info', 'Câmera ativa', { engine: engine.kind });
    } catch (err) {
      console.error('[check-in] câmera:', err);
      timed.end('error', 'Falha ao abrir câmera', { error: errorToText(err) });
      await stopCamera();
      setLastResult({ success: false, message: getCameraErrorMessage(err) });
      setSearchOpen(true);
    } finally {
      setScanning(false);
      setCameraMounting(false);
    }
  }, [
    armNoDecodeWatchdog,
    cameraMounting,
    cameraOn,
    clearNoDecodeWatchdog,
    enqueueScan,
    readerElementId,
    stopCamera,
  ]);

  useEffect(() => () => {
    void stopCamera();
    if (resultClearTimer.current) clearTimeout(resultClearTimer.current);
    clearNoDecodeWatchdog();
  }, [clearNoDecodeWatchdog, stopCamera]);

  const loadParticipants = useCallback(async () => {
    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    setSearchLoading(true);
    try {
      const data =
        parishMode && eventId
          ? await parishFetchCheckInParticipants(eventId, {
              search: searchTerm || undefined,
              filter,
              limit: 200,
            })
          : await fetchCheckInParticipants({
              search: searchTerm || undefined,
              filter,
              limit: 200,
            });
      if (!controller.signal.aborted) setParticipants(data.participants || []);
    } catch {
      if (!controller.signal.aborted) setParticipants([]);
    } finally {
      if (!controller.signal.aborted) setSearchLoading(false);
    }
  }, [eventId, filter, parishMode, searchTerm]);

  useEffect(() => {
    if (!searchOpen) return;
    const t = setTimeout(() => void loadParticipants(), 300);
    return () => clearTimeout(t);
  }, [loadParticipants, searchOpen, searchTerm, filter]);

  const handleManualToggle = async (p: CheckInParticipant) => {
    setBusy(true);
    try {
      let result: CheckInScanResult;
      if (p.checkedIn) {
        result =
          parishMode && eventId
            ? await parishUndoCheckIn(eventId, p.id)
            : await undoCheckIn(p.id);
      } else {
        result =
          parishMode && eventId
            ? await parishManualCheckIn(eventId, p.id)
            : await manualCheckIn(p.id);
      }
      showResult(result);
      onStatsRefresh?.();
      void loadParticipants();
    } catch (err: any) {
      showResult(err?.response?.data || { success: false, message: 'Erro na operação.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Ler QR Code ou código
          </Typography>
          {cameraOn && (
            <Chip
              size="small"
              icon={
                scanStatus === 'processing' || scanStatus === 'detected' ? (
                  <HourglassTopIcon />
                ) : (
                  <QrCode2Icon />
                )
              }
              label={
                scanStatus === 'processing' || scanStatus === 'detected'
                  ? 'Validando…'
                  : busy
                    ? 'Aguarde…'
                    : 'Aponte o QR'
              }
              color={scanStatus !== 'idle' ? 'warning' : 'default'}
              sx={{ fontWeight: 700 }}
            />
          )}
        </Stack>
        {cameraEnvHint && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {cameraEnvHint}
          </Alert>
        )}

        {/* Aviso SEMPRE acima da câmera — visível no mobile */}
        <Box ref={resultBannerRef} sx={{ mb: statusFlash || lastResult ? 1.25 : 0 }}>
          {statusFlash && !lastResult && (
            <Alert
              severity="info"
              icon={<HourglassTopIcon />}
              sx={{
                alignItems: 'center',
                py: 1.25,
                borderRadius: 2.5,
                border: '2px solid',
                borderColor: 'info.main',
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography fontWeight={800} fontSize="1.05rem">
                {statusFlash}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Não tire o ingresso ainda — estamos confirmando.
              </Typography>
            </Alert>
          )}
          {lastResult && (
            <Alert
              severity={
                lastResult.success
                  ? 'success'
                  : lastResult.error === 'ALREADY_CHECKED_IN'
                    ? 'warning'
                    : 'error'
              }
              icon={lastResult.success ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
              onClose={() => {
                setLastResult(null);
                setStatusFlash(null);
                resumeScanner(cameraEngineRef.current);
              }}
              sx={{
                alignItems: 'flex-start',
                py: 1.5,
                borderRadius: 2.5,
                border: '2px solid',
                borderColor: lastResult.success
                  ? 'success.main'
                  : lastResult.error === 'ALREADY_CHECKED_IN'
                    ? 'warning.main'
                    : 'error.main',
                boxShadow: 3,
                '& .MuiAlert-message': { width: '100%' },
                '& .MuiAlert-icon': { fontSize: 32, mt: 0.25 },
              }}
            >
              <Typography fontWeight={800} fontSize={{ xs: '1.1rem', sm: '1.15rem' }} lineHeight={1.25}>
                {lastResult.success
                  ? 'Check-in confirmado!'
                  : lastResult.error === 'ALREADY_CHECKED_IN'
                    ? 'Já fez check-in'
                    : 'Não foi possível'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.35, opacity: 0.95 }}>
                {lastResult.message}
              </Typography>
              {lastResult.data && (
                <Box sx={{ mt: 1 }}>
                  {Boolean(lastResult.data.participantName) && (
                    <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.2rem', lineHeight: 1.2 }}>
                      {String(lastResult.data.participantName)}
                    </Typography>
                  )}
                  {Boolean(lastResult.data.ticketType || lastResult.data.ticketName) && (
                    <Typography variant="body2" sx={{ mt: 0.25 }}>
                      Ingresso: {String(lastResult.data.ticketType || lastResult.data.ticketName)}
                    </Typography>
                  )}
                  {Boolean(lastResult.data.email) && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.85, wordBreak: 'break-all' }}>
                      {String(lastResult.data.email)}
                    </Typography>
                  )}
                </Box>
              )}
              {!lastResult.success && (
                <Button
                  size="small"
                  variant="contained"
                  color={lastResult.error === 'ALREADY_CHECKED_IN' ? 'warning' : 'error'}
                  sx={{ mt: 1.25, fontWeight: 700 }}
                  onClick={() => {
                    setLastResult(null);
                    resumeScanner(cameraEngineRef.current);
                  }}
                >
                  Ler próximo
                </Button>
              )}
            </Alert>
          )}
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Cole o código ou use o leitor USB"
          value={codeInput}
          disabled={busy}
          onChange={(e) => {
            const val = e.target.value;
            setCodeInput(val);
            if (scanInputTimer.current) clearTimeout(scanInputTimer.current);
            scanInputTimer.current = setTimeout(() => {
              const code = normalizeCode(val);
              if (code.length >= 32 || /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(code)) {
                enqueueScan(code);
              }
            }, 180);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              enqueueScan(codeInput);
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => enqueueScan(codeInput)} disabled={busy || !codeInput}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          fullWidth
          variant={cameraOn ? 'outlined' : 'contained'}
          sx={{ mt: 1.5, bgcolor: cameraOn ? undefined : primaryColor, py: 1.1, fontWeight: 700 }}
          startIcon={scanning ? <CircularProgress size={18} color="inherit" /> : <QrCodeScannerIcon />}
          onClick={() => void startCamera()}
          disabled={scanning}
        >
          {cameraOn ? 'Parar câmera' : 'Usar câmera'}
        </Button>
        {cameraOn && cameraEngineKind && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
            Motor: {cameraEngineKind === 'barcode-detector' ? 'nativo (melhor em tela)' : 'html5-qrcode'}
            {liveDecodeCount > 0 ? ` · leituras: ${liveDecodeCount}` : ' · aguardando QR…'}
          </Typography>
        )}
        {noDecodeHint && cameraOn && (
          <Alert
            severity="warning"
            sx={{ mt: 1.25, borderRadius: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<PersonSearchIcon />}
                onClick={() => {
                  setSearchOpen(true);
                  requestAnimationFrame(() => {
                    document.getElementById('checkin-name-search')?.focus();
                  });
                }}
                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                Buscar
              </Button>
            }
          >
            <Typography fontWeight={700} variant="body2">
              Ainda sem leitura do QR
            </Typography>
            <Typography variant="caption" display="block">
              Peça para aumentar o brilho da tela, ou use a busca por nome abaixo.
            </Typography>
          </Alert>
        )}
        <Box
          sx={{
            mt: 1.5,
            width: '100%',
            position: 'relative',
            minHeight: cameraOn || cameraMounting ? { xs: 260, sm: 320 } : 0,
            maxHeight: cameraOn || cameraMounting ? { xs: 340, sm: 420 } : 0,
            overflow: 'hidden',
            opacity: cameraOn || cameraMounting ? 1 : 0,
            pointerEvents: cameraOn || cameraMounting ? 'auto' : 'none',
            borderRadius: 2,
            bgcolor: cameraOn || cameraMounting ? '#111' : 'transparent',
          }}
        >
          <Box
            id={readerElementId}
            ref={qrRef}
            sx={{
              width: '100%',
              minHeight: { xs: 260, sm: 320 },
              '& video': {
                borderRadius: 2,
                width: '100%',
                maxHeight: { xs: 340, sm: 420 },
                objectFit: 'cover',
              },
              '& img': { maxHeight: { xs: 340, sm: 420 } },
            }}
          />
          {(scanStatus === 'detected' || scanStatus === 'processing' || busy) && cameraOn && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(15, 23, 42, 0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 1,
                zIndex: 2,
                pointerEvents: 'none',
              }}
            >
              <CircularProgress size={36} sx={{ color: '#fff' }} />
              <Typography color="#fff" fontWeight={800}>
                {statusFlash || 'Processando…'}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      <Button
        variant={noDecodeHint ? 'contained' : 'text'}
        color={noDecodeHint ? 'warning' : 'primary'}
        startIcon={<PersonSearchIcon />}
        onClick={() => setSearchOpen((v) => !v)}
        sx={noDecodeHint ? { fontWeight: 800, py: 1.1 } : undefined}
      >
        {searchOpen ? 'Ocultar busca por nome' : 'Buscar participante por nome'}
      </Button>

      {debugUi && (
        <>
          <Button
            variant="text"
            startIcon={<BugReportIcon />}
            onClick={() => {
              setLogsOpen((v) => !v);
              setLogEntries(getCheckInLogs());
            }}
          >
            {logsOpen
              ? 'Ocultar logs de diagnóstico'
              : `Logs de diagnóstico (${logEntries.length || getCheckInLogs().length})`}
          </Button>

          <Collapse in={logsOpen}>
            <Paper sx={{ p: 2, borderRadius: 3, bgcolor: '#0f172a', color: '#e2e8f0' }}>
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 1 }}>
                Sessão {getCheckInLogSessionId().slice(0, 8)}… — modo debug (`?checkinDebug=1`).
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(exportCheckInLogsText());
                      setFlushStatus('Copiado para a área de transferência');
                    } catch {
                      setFlushStatus('Não foi possível copiar');
                    }
                  }}
                >
                  Copiar
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
                  onClick={async () => {
                    const res = await flushCheckInLogs();
                    setFlushStatus(res.ok ? `Enviado ${res.sent} log(s)` : 'Falha ao enviar — tente de novo');
                    setLogEntries(getCheckInLogs());
                  }}
                >
                  Enviar agora
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DeleteOutlineIcon />}
                  sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
                  onClick={() => {
                    clearCheckInLogsLocal();
                    setLogEntries([]);
                    setFlushStatus('Logs locais limpos');
                  }}
                >
                  Limpar
                </Button>
              </Stack>
              {flushStatus && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#86efac' }}>
                  {flushStatus}
                </Typography>
              )}
              <Box
                sx={{
                  maxHeight: 240,
                  overflow: 'auto',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 11,
                  lineHeight: 1.45,
                }}
              >
                {(logEntries.length ? logEntries : getCheckInLogs())
                  .slice(-80)
                  .reverse()
                  .map((e, idx) => (
                    <Box key={`${e.ts}-${idx}`} sx={{ mb: 0.75, opacity: e.level === 'debug' ? 0.7 : 1 }}>
                      <Box
                        component="span"
                        sx={{
                          color:
                            e.level === 'error' ? '#fca5a5' : e.level === 'warn' ? '#fcd34d' : '#93c5fd',
                        }}
                      >
                        {e.ts.slice(11, 23)} [{e.level}] {e.event}
                      </Box>
                      {e.durationMs != null && (
                        <Box component="span" sx={{ color: '#a78bfa' }}>
                          {' '}
                          {e.durationMs}ms
                        </Box>
                      )}
                      <Box component="div">{e.message}</Box>
                      {e.data && (
                        <Box component="div" sx={{ color: '#94a3b8', wordBreak: 'break-all' }}>
                          {JSON.stringify(e.data)}
                        </Box>
                      )}
                    </Box>
                  ))}
                {!logEntries.length && !getCheckInLogs().length && (
                  <Typography variant="body2" sx={{ opacity: 0.6 }}>
                    Ainda sem eventos. Abra a câmera ou leia um QR.
                  </Typography>
                )}
              </Box>
            </Paper>
          </Collapse>
        </>
      )}

      <Collapse in={searchOpen}>
        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <TextField
            id="checkin-name-search"
            fullWidth
            size="small"
            placeholder="Nome, e-mail ou telefone"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 1.5 }}
            autoFocus={noDecodeHint}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filter}
            onChange={(_, v) => v && setFilter(v)}
            sx={{ mb: 1.5 }}
          >
            <ToggleButton value="all">Todos</ToggleButton>
            <ToggleButton value="pending">Pendentes</ToggleButton>
            <ToggleButton value="checked">Check-in</ToggleButton>
          </ToggleButtonGroup>
          {searchLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack spacing={1} sx={{ maxHeight: 320, overflow: 'auto' }}>
              {participants.map((p) => (
                <Paper key={p.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {p.name || 'Sem nome'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {p.ticketName} · {p.email || p.phone || '-'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {p.checkedIn && <Chip size="small" color="success" label="OK" />}
                      <Button
                        size="small"
                        variant={p.checkedIn ? 'outlined' : 'contained'}
                        disabled={busy}
                        onClick={() => void handleManualToggle(p)}
                        sx={!p.checkedIn ? { bgcolor: primaryColor } : undefined}
                      >
                        {p.checkedIn ? 'Desfazer' : 'Marcar'}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
              {!participants.length && (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                  Nenhum participante encontrado
                </Typography>
              )}
            </Stack>
          )}
        </Paper>
      </Collapse>
    </Stack>
  );
};

export default EventCheckInScanner;

function CheckInStatsBar({ stats, primaryColor = '#4b1b7a' }: { stats: CheckInStats | null; primaryColor?: string }) {
  if (!stats) return null;
  return (
    <GridStats stats={stats} primaryColor={primaryColor} />
  );
}

function GridStats({ stats, primaryColor }: { stats: CheckInStats; primaryColor: string }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 1,
        mb: 2,
      }}
    >
      {[
        { label: 'Total', value: stats.totalRegistrations },
        { label: 'Check-in', value: stats.checkedInCount, highlight: true },
        { label: 'Pendentes', value: stats.pendingCheckIn },
      ].map((item) => (
        <Paper key={item.label} sx={{ p: 1.5, borderRadius: 2, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {item.label}
          </Typography>
          <Typography variant="h6" fontWeight={800} sx={{ color: item.highlight ? primaryColor : undefined }}>
            {item.value}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

export { EventCheckInScanner, CheckInStatsBar };
